import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Copy, ThumbsUp, ThumbsDown, ArrowUpCircle, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPersonalityTheme } from '../../CardFiles/theme-config';
import './CoachTab.css';

// ─── Helpers ───────────────────────────────────────────────────────────────

const formatTimestamp = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};



// ─── Rich Markdown Renderer ──────────────────────────────────────────────────

const applyInlineFormatting = (text) => {
    if (!text) return '';
    let out = text;
    // Bold: **text**
    out = out.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic: *text* (but not inside bold)
    out = out.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    // Inline code: `code`
    out = out.replace(/`(.*?)`/g, '<code class="coach-inline-code">$1</code>');
    return out;
};

const renderMarkdown = (text, onAction) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // ── Empty line → spacer ──
        if (trimmed === '') {
            elements.push(<div key={i} className="coach-markdown-spacer" />);
            i++;
            continue;
        }

        // ── Action button: [button:Label text] ──
        const btnMatch = trimmed.match(/^\[button:(.*?)\]$/);
        if (btnMatch) {
            const label = btnMatch[1];
            elements.push(
                <button
                    key={i}
                    className="coach-action-button"
                    onClick={() => onAction?.(label)}
                >
                    {label}
                </button>
            );
            i++;
            continue;
        }

        // ── Numbered list: "1. item", "2. item", etc. ──
        const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
        if (numMatch) {
            // Collect consecutive numbered items
            const items = [];
            while (i < lines.length) {
                const nLine = lines[i].trim();
                const nMatch = nLine.match(/^(\d+)\.\s+(.+)/);
                if (!nMatch) break;
                items.push({ num: nMatch[1], text: nMatch[2] });
                i++;
            }
            elements.push(
                <div key={`ol-${i}`} className="coach-numbered-list">
                    {items.map((item, idx) => (
                        <div key={idx} className="coach-numbered-item">
                            <span className="coach-number-badge">{item.num}</span>
                            <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(item.text) }} />
                        </div>
                    ))}
                </div>
            );
            continue;
        }

        // ── Bullet list: "- item" or "* item" ──
        const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('* ');
        if (isBullet) {
            // Collect consecutive bullet items
            const items = [];
            while (i < lines.length) {
                const bLine = lines[i].trim();
                if (!bLine.startsWith('- ') && !bLine.startsWith('* ')) break;
                items.push(bLine.substring(2));
                i++;
            }
            elements.push(
                <div key={`ul-${i}`} className="coach-bullet-list">
                    {items.map((item, idx) => (
                        <div key={idx} className="coach-markdown-list-item">
                            <span className="coach-list-dot" />
                            <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(item) }} />
                        </div>
                    ))}
                </div>
            );
            continue;
        }

        // ── Regular paragraph line ──
        elements.push(
            <div
                key={i}
                className="coach-markdown-line"
                dangerouslySetInnerHTML={{ __html: applyInlineFormatting(trimmed) }}
            />
        );
        i++;
    }

    return elements;
};


// ─── TypingIndicator ─────────────────────────────────────────────────────────

const TypingIndicator = () => (
    <div className="coach-typing">
        <div className="coach-typing-dot" />
        <div className="coach-typing-dot" />
        <div className="coach-typing-dot" />
    </div>
);


// ─── CoachMessage ─────────────────────────────────────────────────────────────

const CoachMessage = ({ message, isCoach, showHeader, onAction }) => {
    const [liked, setLiked] = useState(null); // null | 'up' | 'down'
    const ts = formatTimestamp(message.timestamp);

    const handleCopy = () => {
        navigator.clipboard.writeText(message.content).catch(() => {});
    };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className={`coach-message ${isCoach ? 'coach-msg' : 'user-msg'} ${showHeader ? 'with-header' : 'no-header'}`}
        >
            {isCoach && showHeader && (
                <div className="coach-message-sender">
                    <span className="coach-sender-dot" />
                    <Sparkles size={12} className="coach-sparkle" />
                    Coach
                </div>
            )}
            <div className="coach-bubble-wrap">
                <div className="coach-bubble">
                    {renderMarkdown(message.content, onAction)}
                </div>
            </div>
            {isCoach ? (
                <div className="coach-message-actions">
                    <button
                        className={`coach-action-btn ${liked === 'up' ? 'active' : ''}`}
                        onClick={() => setLiked(liked === 'up' ? null : 'up')}
                        title="Helpful"
                    >
                        <ThumbsUp size={13} />
                    </button>
                    <button
                        className={`coach-action-btn ${liked === 'down' ? 'active' : ''}`}
                        onClick={() => setLiked(liked === 'down' ? null : 'down')}
                        title="Not helpful"
                    >
                        <ThumbsDown size={13} />
                    </button>
                    <button className="coach-action-btn" onClick={handleCopy} title="Copy">
                        <Copy size={13} />
                    </button>
                    {ts && <span className="coach-msg-timestamp">{ts}</span>}
                </div>
            ) : (
                ts && <div className="coach-msg-timestamp user-ts">{ts}</div>
            )}
        </motion.div>
    );
};

// ─── SuggestedPrompts ────────────────────────────────────────────────────────

const SuggestedPrompts = ({ suggestions, loading, onSelect, analysisResult }) => {
    const pCard = analysisResult?.cards_data?.personality_card ||
        analysisResult?.personality_card || {};
    const personaKey = pCard?.persona_key || pCard?.persona?.toLowerCase() || 'visionary';
    const theme = getPersonalityTheme(personaKey);
    const accent = theme?.primary || '#a855f7';
    const accentSoft = theme?.accent || '#c4b5fd';

    if (loading) {
        return (
            <div className="coach-suggestions-wrapper">
                <div className="coach-suggestions-label">Suggested for you</div>
                {[1, 2, 3].map(i => (
                    <div key={i} className="coach-suggestion-skeleton" />
                ))}
            </div>
        );
    }

    if (!suggestions || suggestions.length === 0) return null;

    return (
        <div className="coach-suggestions-wrapper">
            <div className="coach-suggestions-label">Suggested for you</div>
            {suggestions.map((s, i) => (
                <motion.button
                    key={i}
                    whileHover={{ scale: 1.01, x: 4 }}
                    whileTap={{ scale: 0.99 }}
                    className="coach-suggestion-chip"
                    style={{
                        '--chip-accent': accent,
                        '--chip-accent-soft': accentSoft,
                    }}
                    onClick={() => onSelect(s)}
                >
                    <span className="coach-suggestion-text">{s}</span>
                    <ArrowUpCircle size={16} className="coach-suggestion-arrow" />
                </motion.button>
            ))}
        </div>
    );
};

// ─── CoachInput ──────────────────────────────────────────────────────────────

const CoachInput = ({ onSend, disabled }) => {
    const [text, setText] = useState('');
    const textareaRef = useRef(null);

    // Re-focus after coach reply (true→false transition only, not on mount)
    const prevDisabledRef = useRef(null);
    useEffect(() => {
        if (prevDisabledRef.current === true && !disabled && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 100);
        }
        prevDisabledRef.current = disabled;
    }, [disabled]);

    const autoResize = () => {
        const el = textareaRef.current;
        if (el) {
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 100) + 'px';
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed || disabled) return;
        onSend(trimmed);
        setText('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    // Focus on pill click so tapping anywhere in the input area activates the textarea
    const handlePillClick = (e) => {
        if (e.target.closest('.coach-send-btn')) return; // don't steal from send button
        textareaRef.current?.focus();
    };

    return (
        <div className="coach-input-area">
            <div className="coach-input-pill" onClick={handlePillClick}>
                <textarea
                    ref={textareaRef}
                    className="coach-textarea"
                    placeholder="Write to Coach"
                    value={text}
                    rows={1}
                    onChange={(e) => { setText(e.target.value); autoResize(); }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                />
                <button
                    className="coach-send-btn"
                    onClick={handleSend}
                    disabled={!text.trim() || disabled}
                    title="Send"
                >
                    <Send size={14} />
                </button>
            </div>
        </div>
    );
};

// ─── CoachTab (Main) ─────────────────────────────────────────────────────────

const CoachTab = ({ user, analysisResult }) => {
    const [messages, setMessages] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(true);
    const [isTyping, setIsTyping] = useState(false);
    const bottomRef = useRef(null);
    const hasMessages = messages.length > 0;

    // Scroll to bottom on new messages
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // When keyboard opens, dvh shrinks → scroll to keep last message visible
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        const onResize = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
        vv.addEventListener('resize', onResize);
        return () => vv.removeEventListener('resize', onResize);
    }, []);

    // Fetch personalized suggestions on mount
    useEffect(() => {
        if (!user?.id) return;
        setSuggestionsLoading(true);
        fetch(`/api/coach/suggestions?user_id=${user.id}`)
            .then(r => r.json())
            .then(data => {
                if (data.success && data.suggestions) {
                    setSuggestions(data.suggestions);
                }
            })
            .catch(err => console.error('Failed to load suggestions:', err))
            .finally(() => setSuggestionsLoading(false));
    }, [user?.id]);

    // Initial welcome message from Coach
    useEffect(() => {
        const name = user?.firstName || user?.name?.split(' ')?.[0] || 'there';

        // Collect archetypes from all three card types
        const pCard = analysisResult?.cards_data?.personality_card || analysisResult?.personality_card || {};
        const cCard = analysisResult?.cards_data?.character_card  || analysisResult?.character_card  || {};
        const gCard = analysisResult?.cards_data?.growth_card     || analysisResult?.growth_card     || {};

        // Convert camelCase to Title Case words (e.g., "reflectiveIntrovert" → "Reflective Introvert")
        // Used to fix unformatted traits/personas from the backend
        const camelToTitle = (str = '') =>
            str.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();

        const normaliseWord = (str) => {
            if (!str) return null;
            // If it contains a capital letter not at the start, it's likely camelCase
            if (/[a-z][A-Z]/.test(str)) {
                return camelToTitle(str);
            }
            return str;
        };

        const resolve = (card) => {
            if (!card) return null;
            if (card.persona) return normaliseWord(card.persona);
            if (card.persona_key) return normaliseWord(card.persona_key);
            return null;
        };

        const archetypes = [resolve(pCard), resolve(cCard), resolve(gCard)].filter(Boolean);

        // Extract dynamically collected traits from the user's data
        const baseSource = analysisResult?.cards_data || analysisResult || {};
        const rawTraits = pCard?.keyInsights || baseSource?.insights?.top_traits || baseSource?.trait_analysis?.top_traits || [];
        const traits = Array.isArray(rawTraits) ? rawTraits.filter(t => typeof t === 'string').map(t => t.toLowerCase()) : [];

        let greet;
        const mainPersona = archetypes[0];

        if (mainPersona && traits.length >= 2) {
            greet = `Hey ${name}! As ${mainPersona}, your ${traits[0]} and ${traits[1]} instincts are a real strength. What's on your mind?`;
        } else if (mainPersona && traits.length === 1) {
            greet = `Hey ${name}! As ${mainPersona}, being ${traits[0]} is a real strength. What's on your mind?`;
        } else if (traits.length >= 2) {
            greet = `Hey ${name}! Your ${traits[0]} and ${traits[1]} nature is a rare mix. What are we working on?`;
        } else if (archetypes.length === 2) {
            greet = `Hey ${name}! ${archetypes[0]} meets ${archetypes[1]} — that's a rare mix. What's on your mind?`;
        } else if (archetypes.length >= 3) {
            greet = `Hey ${name}! ${archetypes[0]}, ${archetypes[1]}, ${archetypes[2]} — you've got range. What's on your mind?`;
        } else if (mainPersona) {
            greet = `Hey ${name}! Your ${mainPersona} instincts are a real strength. What's on your mind?`;
        } else {
            greet = `Hey ${name}! I'm your coach — what's on your mind?`;
        }

        setMessages([{
            id: 'welcome',
            role: 'assistant',
            content: greet,
            timestamp: new Date().toISOString()
        }]);
    }, [user, analysisResult]);


    const sendMessage = useCallback(async (text) => {
        const userMsg = {
            id: `u_${Date.now()}`,
            role: 'user',
            content: text,
            timestamp: new Date().toISOString()
        };

        // Optimistically add user message + hide suggestions
        setMessages(prev => [...prev, userMsg]);
        setSuggestions([]);
        setIsTyping(true);

        try {
            // Build history from current messages (excluding welcome)
            const history = messages
                .filter(m => m.id !== 'welcome')
                .map(m => ({ role: m.role, content: m.content }));

            const response = await fetch('/api/coach/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user?.id,
                    message: text,
                    history,
                    profile: analysisResult || {}
                })
            });
            const data = await response.json();

            const coachMsg = {
                id: `c_${Date.now()}`,
                role: 'assistant',
                content: data.reply || "I'm here — try again in a moment.",
                timestamp: data.timestamp || new Date().toISOString()
            };
            setMessages(prev => [...prev, coachMsg]);
        } catch (err) {
            console.error('Coach chat error:', err);
            setMessages(prev => [...prev, {
                id: `err_${Date.now()}`,
                role: 'assistant',
                content: "Sorry, I hit a snag. Check your connection and try again.",
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsTyping(false);
        }
    }, [messages, user, analysisResult]);

    const handleSuggestionSelect = useCallback((text) => {
        sendMessage(text);
    }, [sendMessage]);

    const showSuggestions = !hasMessages || messages.filter(m => m.role === 'user').length === 0;

    return (
        <div className="coach-tab">
            <div className="coach-message-list">
                
                {messages.map((msg, idx) => {
                    const prevMsg = messages[idx - 1];
                    const showHeader = !prevMsg || prevMsg.role !== msg.role;
                    
                    return (
                        <CoachMessage
                            key={msg.id}
                            message={msg}
                            isCoach={msg.role === 'assistant'}
                            showHeader={showHeader}
                            onAction={sendMessage}
                        />
                    );
                })}

                <AnimatePresence>
                    {isTyping && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="coach-message coach-msg no-header"
                        >
                            <TypingIndicator />
                        </motion.div>
                    )}
                </AnimatePresence>

                {showSuggestions && (
                    <div style={{ marginTop: '12px' }}>
                        <SuggestedPrompts
                            suggestions={suggestions}
                            loading={suggestionsLoading}
                            onSelect={handleSuggestionSelect}
                            analysisResult={analysisResult}
                        />
                    </div>
                )}

                <div ref={bottomRef} />
            </div>

            <div className="coach-footer-area">
                <CoachInput onSend={sendMessage} disabled={isTyping} />
            </div>
        </div>
    );
};

export default CoachTab;
